import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { LocalStorage } from '../auth/local.storage';
import { AccountService } from '../auth/account.service';
import { StringHelper } from 'src/app/utils/helpers';
import { isK3000domain } from 'src/app/core/common';
import { environment } from 'src/environments/environment';
import { KeyVaultService } from '../key-vault.service';
import { KeyVaultKeys } from 'src/app/utils/enums/key-vault-keys.enum';
import { IStiAuthInfo } from './sti.models';

@Injectable({
  providedIn: 'root'
})
export class StiAuthService {
  private readonly STI_INFO_KEY_PREFIX = 'STI_INFO_KEY_';
  private readonly STI_STATE_KEY_PREFIX = 'STI_STATE_KEY_';
  private readonly WINDOW_NAME = 'StiAuthPopup';
  private readonly WINDOW_SETTINGS = 'dialog=yes,dependent=yes,scrollbars=yes,location=yes';

  private readonly authUrl = environment.sti.baseUrl + 'auth/authorize/';
  private readonly tokenUrl = environment.sti.baseUrl +  'auth/token/';
  private readonly name = environment.sti.name;

  private readonly localStorage = inject(LocalStorage);
  private readonly http = inject(HttpClient);
  private readonly accountService = inject(AccountService);
  private readonly keyVaultService = inject(KeyVaultService);

  private refreshTokenTimeout: any;

  get clientId(): string {
    return isK3000domain() ? environment.sti.clientId_kurzweil : environment.sti.clientId_alinea;
  }

  public getToken(): string | undefined {
    const authInfo = this.getAuthInfo();
    return authInfo?.access_token;
  }

  public getAuthorizationHeaderValue(): string {
    const authInfo = this.getAuthInfo();
    if (!authInfo?.access_token) {
      throw new Error('Authentication failed: no STI token available.');
    }

    const tokenType = authInfo.token_type || 'Bearer';
    return `${tokenType} ${authInfo.access_token}`;
  }

  public async authorize(): Promise<void> {
    this.logout();

    const authWindow = window.open('', this.WINDOW_NAME, this.WINDOW_SETTINGS);
    if (!authWindow) {
      throw new Error('Popup blocked by browser.');
    }

    await this.handleAuthWindow(authWindow);
  }

  public logout(): void {
    this.stopRefreshTokenTimer();
    this.localStorage.remove(this.getStorageKey());
    this.localStorage.remove(this.getStateKey());
  }

  public async fetchAuthInfo(code: string, state: string): Promise<IStiAuthInfo> {
    const savedState = this.localStorage.getWithExpiry(this.getStateKey()) as string;
    this.localStorage.remove(this.getStateKey());

    // Validate state only here to keep callback component free of auth checks.
    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter. Possible attack.');
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', code);
    body.set('redirect_uri', this.getRedirectUri());
    body.set('client_id', this.clientId);
    body.set('client_secret', await this.getSecretFromKeyVault());

    return firstValueFrom(
      this.http.post<IStiAuthInfo>(this.tokenUrl, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
    );
  }

  private handleAuthWindow(authWindow: Window): Promise<void> {
    return new Promise((resolve, reject) => {
      authWindow.location.href = this.getFullAuthUrl();

      let intCheckId: any;

      const cleanup = () => {
        window.removeEventListener('message', messageListener);
        if (intCheckId) clearInterval(intCheckId);
      }

      const messageListener = (e: MessageEvent) => {
        // Ignore unrelated messages
        if (window.location.origin !== e.origin || e.data === undefined) {
          return; 
        }

        cleanup();

        if (e.data.error) {
          return reject(new Error(`STI authorization failed: ${e.data.error}`));
        }

        this.setNewAuthInfo(e.data as IStiAuthInfo);
        resolve();
      };
      window.addEventListener('message', messageListener, false);

      intCheckId = setInterval(() => {
        if (authWindow.closed) {
          cleanup();
          this.localStorage.remove(this.getStateKey());
          reject(new Error('STI authorization window was closed by the user.'));
        }
      }, 500);
    });
  }

  private refreshToken(authInfo: IStiAuthInfo): Observable<IStiAuthInfo> {
    return from(this.getSecretFromKeyVault()).pipe(
      switchMap(secret => {
        const body = new URLSearchParams();
        body.set('grant_type', 'refresh_token');
        body.set('refresh_token', authInfo.refresh_token);
        body.set('redirect_uri', this.getRedirectUri());
        body.set('client_id', this.clientId);
        body.set('client_secret', secret);

        return this.http.post<IStiAuthInfo>(this.tokenUrl, body.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
      }),
      tap(response => {
        this.setNewAuthInfo(response);
      })
    );
  }

  private startRefreshTokenTimer(authInfo: IStiAuthInfo): void {
    // NOTE 14 JUL 2026, in case expires_in is less then 60s
    const timeout = (Math.max(authInfo.expires_in, 61) - 60) * 1000;

    this.stopRefreshTokenTimer();

    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshToken(authInfo).subscribe({
        next: () => console.log(`STI token (${this.name}) refreshed successfully`),
        error: err => console.error(`Error refreshing STI token (${this.name})`, err)
      });
    }, timeout);
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
  }

  private setNewAuthInfo(authInfo: IStiAuthInfo): void {
    this.localStorage.setWithExpiry<IStiAuthInfo>(
      this.getStorageKey(),
      authInfo,
      authInfo?.expires_in ? authInfo.expires_in * 1000 : 3600 * 1000
    );
    this.startRefreshTokenTimer(authInfo);
  }

  private getStorageKey(): string {
    return this.STI_INFO_KEY_PREFIX + this.accountService.userId;
  }

  private getAuthInfo(): IStiAuthInfo | null {
    return this.localStorage.getWithExpiry(this.getStorageKey()) as IStiAuthInfo | null;
  }

  private getFullAuthUrl(): string {
    const params = new URLSearchParams();
    params.set('response_type', 'code');
    params.set('client_id', this.clientId);
    params.set('redirect_uri', this.getRedirectUri());
    params.set('state', this.createAndStoreState());

    return `${this.authUrl}?${params.toString()}`;
  }

  private createAndStoreState(): string {
    const stateId = StringHelper.random(16);
    const state = `${this.name}:${stateId}`;

    this.localStorage.setWithExpiry<string>(this.getStateKey(), state, 60 * 60 * 1000);

    return state;
  }

  private getStateKey(): string {
    return this.STI_STATE_KEY_PREFIX + this.accountService.userId;
  }

  private getRedirectUri(): string {
    return location.origin + '/sti-callback';
  }

  private async getSecretFromKeyVault(): Promise<string> {
    const key = isK3000domain() ? KeyVaultKeys.KurzweilSti : KeyVaultKeys.AlineaSti
    const response = await this.keyVaultService.getCredentials([key]);
    const secret = response[KeyVaultKeys[key]]?.secret
    if (secret) return secret;
    console.error(`STI secret not found in Key Vault for ${this.name}`);
    return '';
  }
}
